import { useState, useEffect, Children } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { animated, useSpring, useTrail, a, config as springConfig } from '@react-spring/web';
import { useTranslation } from 'next-i18next';
import { useInView } from 'react-intersection-observer';

import ContentSectionStyle from './style';

import Image from 'src/components/atoms/image';
import config from 'src/config';
import { useWindowSize } from 'src/helpers/use';
import appActions from 'src/redux/app/actions';
import { B2BContentStatisticType } from 'src/redux/app/reducer';
import { RootState } from 'src/redux/reducer';

const { fetch_b2b_content_statistic } = appActions;

const STAT_DATA = [
  {
    key: 'course_online',
    suffix: '+',
  },
  {
    key: 'leading_experts',
    suffix: '+',
  },
  {
    key: 'content_category',
    suffix: '',
  },
];

const CONTENT_DATA = [
  {
    name: 'Functional Competency',
    categories: [
      'Sales',
      'Service Mind',
      'Negotiation',
      'Accounting',
      'Digital Marketing',
      'Finance',
      'Programming',
      'Human Resource',
      'more…',
    ],
  },
  {
    name: 'Business',
    categories: [
      'Business Model',
      'Strategy',
      'Project Management',
      'Organization Development',
      'Corporate Innovation',
      'more…',
    ],
  },
  {
    name: 'People',
    categories: [
      'Coaching',
      'Conflict Management',
      'Leadership',
      'Management and Supervision',
      'Positive Thinking',
      'Problem Solving',
      'more…',
    ],
  },
  {
    name: 'Data and Digital',
    categories: [
      'Data Management',
      'Data Visualization',
      'Digital Literacy',
      'Excel for Business',
      'PDPA',
      'Network Operation',
      'Digital Transformation',
      'Microsoft office',
      'more…',
    ],
  },
  {
    name: 'Individual Development',
    categories: [
      'Computer Programming',
      'Video & Graphic Design',
      'Investment',
      'Lifestyle',
      'Tax',
      'Personal Branding',
      'Health & Wellness',
      'E-Commerce',
      'more…',
    ],
  },
  {
    name: 'Book Summaries',
    categories: [
      'Business',
      'Food For Thoughts',
      'Change Management',
      'Creativity',
      'Critical Thinking',
      'Influence',
      'Marketing',
      'Mindset',
      'Relationship',
      'more…',
    ],
  },
];

type FadedInOutAnimatePropType = {
  open: boolean;
  children: object;
};

const FadedInOutAnimate = ({ open, children }: FadedInOutAnimatePropType) => {
  const styles = useSpring({ opacity: open ? 1 : 0, delay: 100 });
  return <animated.div style={{ ...styles, position: 'absolute', top: 0, width: '95%' }}>{children}</animated.div>;
};

type FadeRightToLeftAnimatePropType = {
  isIn: boolean;
  isOut: boolean;
  children: object;
};

const FadeRightToLeftAnimate = ({ isIn, isOut, children }: FadeRightToLeftAnimatePropType) => {
  const items = Children.toArray(children);
  const trail = useTrail(items.length, {
    opacity: isIn ? 1 : 0,
    x: isIn ? 0 : isOut ? -300 : 300,
    from: { x: 300, opacity: 0 },
    delay: 100,
  });

  return (
    <div>
      {trail.map((style, index) => (
        <a.div key={index} style={style}>
          <a.div>{items[index]}</a.div>
        </a.div>
      ))}
    </div>
  );
};
const ContentSection = () => {
  const { t } = useTranslation('business');
  const dispatch = useDispatch();
  const [tabIndex, setTabIndex] = useState<boolean | number>(false);
  const [prevTabIndex, setPrevTabIndex] = useState<boolean | number>(false);
  const { b2bContentStatistic } = useSelector((state: RootState) => ({
    b2bContentStatistic: state.App.get('b2bContentStatistic'),
  }));
  const [countData, setCountData] = useState<B2BContentStatisticType>(b2bContentStatistic);
  const { val: courseOnlineVal } = useSpring({
    val: countData.course_online,
    config: springConfig.molasses,
    delay: 400,
  });
  const { val: leadingExpertsVal } = useSpring({
    val: countData.leading_experts,
    config: springConfig.molasses,
    delay: 400,
  });
  const { val: contentCategoryVal } = useSpring({
    val: countData.content_category,
    config: springConfig.molasses,
    delay: 400,
  });
  const { ref: courseCardContainerRef, inView: courseCardInView } = useInView({
    threshold: 0.6,
  });
  const { ref: countDataRef, inView: countDataInView } = useInView({
    threshold: 0.9,
  });
  const { width } = useWindowSize();

  const handleChange = (_: any, newValue: number) => {
    setPrevTabIndex(tabIndex);
    setTabIndex(newValue);
  };

  const getInstructorImageUrl = (index: number, size: string = '', fileType: string) =>
    `${config.IMAGE_CDN_HOST}/assets/b2b-landing/content/Instructor-0${index}${size}.${fileType}`;

  const getCourseImageUrl = (categoryIndex: number, index: number, size: string = '', fileType: string) =>
    `${config.IMAGE_CDN_HOST}/assets/b2b-landing/content/0${categoryIndex}-coursecard-0${index}${size}.${fileType}`;

  useEffect(() => {
    if (courseCardInView && tabIndex === false) setTabIndex(0);
  }, [courseCardInView]);

  useEffect(() => {
    dispatch(fetch_b2b_content_statistic());
  }, []);

  useEffect(() => {
    if (countDataInView) {
      setCountData(b2bContentStatistic);
    }
  }, [countDataInView]);

  return (
    <ContentSectionStyle width={width}>
      <div className="container">
        <Grid container className="top-container">
          <Grid item lg={8} xs={12}>
            <Typography className="title-eng">{t('content_subtitle')}</Typography>
            <Typography className="title">{t('content_title')}</Typography>
            <Typography className="subtitle">{t('content_description')}</Typography>
            <Typography className="subtitle">{t('content_description_more')}</Typography>
          </Grid>
          <Grid item lg={4} xs={12} className="stat-container" ref={countDataRef}>
            {STAT_DATA.map((data) => (
              <div className="stat-box" key={data.key}>
                <div className="stat-number">
                  {data.key === 'course_online' && (
                    <animated.div>{courseOnlineVal.to((val: number) => Math.floor(val))}</animated.div>
                  )}
                  {data.key === 'leading_experts' && (
                    <animated.div>{leadingExpertsVal.to((val: number) => Math.floor(val))}</animated.div>
                  )}
                  {data.key === 'content_category' && (
                    <animated.div>{contentCategoryVal.to((val: number) => Math.floor(val))}</animated.div>
                  )}
                  {data.suffix}
                </div>
                <Typography className="stat-title">{t(data.key)}</Typography>
              </div>
            ))}
          </Grid>
        </Grid>

        <Tabs
          value={tabIndex}
          onChange={handleChange}
          variant="scrollable"
          scrollButtons
          allowScrollButtonsMobile
          aria-label="scrollable"
        >
          {CONTENT_DATA.map((content, index) => (
            <Tab label={content.name} key={index} />
          ))}
        </Tabs>
        <Box minHeight="71px">
          {CONTENT_DATA.map((content, index) => (
            <div key={index} role="tabpanel" hidden={tabIndex !== index}>
              <div className="tab-content">
                <div className="category-list">
                  {content.categories.map((category, categoryIndex) => (
                    <div key={categoryIndex} className="category-box">
                      {category}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </Box>

        <Grid container paddingTop={{ xs: 1, sm: 3, lg: 4 }}>
          <Grid item lg={4} sx={{ display: { xs: 'none', lg: 'block' } }} zIndex={1}>
            <Image
              style={{ width: 337, height: 440, position: 'absolute' }}
              src={`${config.IMAGE_CDN_HOST}/assets/b2b-landing/content/ipad.png`}
              alt="ipad"
              // eslint-disable-next-line max-len
              srcSet={`${config.IMAGE_CDN_HOST}/assets/b2b-landing/content/ipad.png, ${config.IMAGE_CDN_HOST}/assets/b2b-landing/content/ipadx2.png 2x`}
              // eslint-disable-next-line max-len
              srcSetWebp={`${config.IMAGE_CDN_HOST}/assets/b2b-landing/content/ipad.webp, ${config.IMAGE_CDN_HOST}/assets/b2b-landing/content/ipadx2.webp 2x`}
            />
            {CONTENT_DATA.map((_, contentIndex) => (
              <FadeRightToLeftAnimate
                isIn={tabIndex === contentIndex}
                isOut={prevTabIndex === contentIndex}
                key={contentIndex}
              >
                <Image
                  style={{
                    width: 337,
                    height: 432,
                    position: 'absolute',
                    marginLeft: 22,
                    paddingBottom: 10,
                  }}
                  src={getInstructorImageUrl(contentIndex + 1, '', 'png')}
                  alt={t(`instructor_0${contentIndex + 1}_alt`)}
                  srcSet={`${getInstructorImageUrl(contentIndex + 1, '', 'png')}, ${getInstructorImageUrl(
                    contentIndex + 1,
                    'x2',
                    'png',
                  )} 2x`}
                  srcSetWebp={`${getInstructorImageUrl(contentIndex + 1, '', 'webp')}, ${getInstructorImageUrl(
                    contentIndex + 1,
                    'x2',
                    'webp',
                  )} 2x`}
                />
              </FadeRightToLeftAnimate>
            ))}
          </Grid>
          <Grid item lg={1} sx={{ display: { xs: 'none', lg: 'block' } }} />
          <Grid item lg={7} xs={12}>
            <Grid container ref={courseCardContainerRef}>
              {Array.from({ length: 4 }, (_, i) => i + 1).map((index: number) => (
                <Grid item xs={6} key={index} className="course-card-container">
                  {CONTENT_DATA.map((_, dataIndex) => (
                    <FadedInOutAnimate open={tabIndex === dataIndex} key={dataIndex}>
                      <Image
                        src={getCourseImageUrl(dataIndex + 1, index, '', 'png')}
                        alt={t(`0${dataIndex + 1}_course_card_0${index}`)}
                        srcSet={`${getCourseImageUrl(dataIndex + 1, index, '', 'png')}, ${getCourseImageUrl(
                          dataIndex + 1,
                          index,
                          'x2',
                          'png',
                        )} 2x`}
                        srcSetWebp={`${getCourseImageUrl(dataIndex + 1, index, '', 'webp')}, ${getCourseImageUrl(
                          dataIndex + 1,
                          index,
                          'x2',
                          'webp',
                        )} 2x`}
                      />
                    </FadedInOutAnimate>
                  ))}
                </Grid>
              ))}
            </Grid>
            <Typography className="remark">{t('and_other_various_course')}</Typography>
          </Grid>
        </Grid>
      </div>
    </ContentSectionStyle>
  );
};

export default ContentSection;
